import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Button,
  Tooltip,
  Box,
  Skeleton,
  Stack,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PatientFlags from "./PatientFlags";
import {
  HEALTH_STATUS_COLORS,
  VISIT_COLORS,
  HEALTH_SEVERITY_WEIGHT,
  VISIT_RECENCY_WEIGHT,
  SP_VISIT_COLORS,
  getSpVisitAge,
} from "../../constants/clinicalDashboard";

const ROWS_PER_PAGE = 50;

/**
 * Tabla de pacientes con triage sort y paginación.
 * Columnas: Patient, Age, Status, Last Visit, Gaps, Conditions, ER, Meds, Provider, Action
 */
export default function PatientTable({ patients, filters, loading, onCreateTask }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  // Reset page when filters change
  React.useEffect(() => { setPage(0); }, [filters]);

  // Filter patients
  const filtered = useMemo(() => {
    if (!patients?.length) return [];

    return patients.filter((p) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!p.name?.toLowerCase().includes(q) && !String(p.cipId).includes(q)) return false;
      }
      if (filters.healthStatus && filters.healthStatus !== "All" && p.healthStatus !== filters.healthStatus) return false;
      if (filters.lastVisit && filters.lastVisit !== "All" && p.lastVisit !== filters.lastVisit) return false;
      if (filters.provider && filters.provider !== "All" && String(p.pcpNpi) !== String(filters.provider)) return false;
      return true;
    });
  }, [patients, filters]);

  // Triage sort: admitted → discharged → severity → no SP visit → visit recency → unworked gaps → gaps → name
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      // 1. Currently admitted first
      if (a.currentInpatient !== b.currentInpatient) return a.currentInpatient ? -1 : 1;
      // 2. Recently discharged
      if (a.recentlyDischarged !== b.recentlyDischarged) return a.recentlyDischarged ? -1 : 1;
      // 3. Health severity weight (desc)
      const sevA = HEALTH_SEVERITY_WEIGHT[a.healthStatus] ?? 0;
      const sevB = HEALTH_SEVERITY_WEIGHT[b.healthStatus] ?? 0;
      if (sevA !== sevB) return sevB - sevA;
      // 4. No SP Visit → prioritize patients without SantePlus visit
      const spA = a.lastSantePlusVisit ? 0 : 1;
      const spB = b.lastSantePlusVisit ? 0 : 1;
      if (spA !== spB) return spB - spA;
      // 5. Visit recency (desc = longer since visit first)
      const visA = VISIT_RECENCY_WEIGHT[a.lastVisit] ?? 0;
      const visB = VISIT_RECENCY_WEIGHT[b.lastVisit] ?? 0;
      if (visA !== visB) return visB - visA;
      // 6. Unworked gaps ratio (patients with more unworked gaps first)
      const unworkedA = (a.openGaps || 0) - (a.careGapsWorked || 0);
      const unworkedB = (b.openGaps || 0) - (b.careGapsWorked || 0);
      if (unworkedA !== unworkedB) return unworkedB - unworkedA;
      // 7. Open gaps (desc)
      if ((a.openGaps || 0) !== (b.openGaps || 0)) return (b.openGaps || 0) - (a.openGaps || 0);
      // 8. Alphabetical
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [filtered]);

  const paged = sorted.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const getRowBorderColor = (p) => {
    if (p.healthStatus === "Complex" || p.healthStatus === "End of Life") return "#DC2626";
    if (p.healthStatus === "At Risk") return "#EA580C";
    return "transparent";
  };

  if (loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Stack>
    );
  }

  return (
    <>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Patient", "Age", "Status", "Last Visit", "Gaps", "Conditions", "ER", "Meds", "SP Visit", "Obs", "Tasks", "Gaps Worked", "Provider", ""].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    color: "text.secondary",
                    py: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No patients match the current filters
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((p) => (
                <TableRow
                  key={p.cipId}
                  hover
                  sx={{
                    borderLeft: `3px solid ${getRowBorderColor(p)}`,
                    "&:hover": { bgcolor: "#F8FAFB" },
                  }}
                >
                  {/* Patient name + flags */}
                  <TableCell sx={{ py: 1, maxWidth: 220 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontSize: "0.78rem",
                          cursor: "pointer",
                          "&:hover": { color: "#2563EB", textDecoration: "underline" },
                        }}
                        onClick={() => navigate(`/ClinicalInfo?cipId=${p.cipId}`)}
                      >
                        {p.name}
                      </Typography>
                      <PatientFlags patient={p} />
                    </Box>
                  </TableCell>

                  {/* Age */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>{p.age}</Typography>
                  </TableCell>

                  {/* Health Status */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <StatusChip status={p.healthStatus} />
                  </TableCell>

                  {/* Last Visit */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <VisitChip visit={p.lastVisit} />
                  </TableCell>

                  {/* Open Gaps */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <GapBadge count={p.openGaps} />
                  </TableCell>

                  {/* Conditions */}
                  <TableCell sx={{ py: 1, maxWidth: 180 }}>
                    {p.conditions ? (
                      <Tooltip title={p.conditions}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.72rem",
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 170,
                          }}
                        >
                          {p.conditions}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>—</Typography>
                    )}
                  </TableCell>

                  {/* ER Events */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <GapBadge count={p.erEvents} isER />
                  </TableCell>

                  {/* Medications */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: (p.medications || 0) >= 5 ? 700 : 400,
                        color: (p.medications || 0) >= 5 ? "#D97706" : "text.primary",
                      }}
                    >
                      {p.medications ?? 0}
                    </Typography>
                  </TableCell>

                  {/* SP Visit */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <SpVisitChip date={p.lastSantePlusVisit} />
                  </TableCell>

                  {/* Observations */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <CountBadge count={p.observationsCount} color="#2563EB" bgColor="#DBEAFE" />
                  </TableCell>

                  {/* Active Tasks */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <CountBadge count={p.activeTasks} color="#D97706" bgColor="#FEF3C7" />
                  </TableCell>

                  {/* Gaps Worked */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <GapsWorkedBadge worked={p.careGapsWorked} total={p.openGaps} />
                  </TableCell>

                  {/* Provider */}
                  <TableCell sx={{ py: 1, maxWidth: 150 }}>
                    <Typography variant="caption" sx={{ fontSize: "0.72rem" }} noWrap>
                      {p.pcpName || "—"}
                    </Typography>
                  </TableCell>

                  {/* Action */}
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View Patient">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/patient-info/${p.cipId}`)}
                          sx={{ color: "#6366F1" }}
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate(`/ClinicalInfo?cipId=${p.cipId}`)}
                        sx={{ fontSize: "0.7rem", textTransform: "none", minWidth: 0, px: 1 }}
                      >
                        Open
                      </Button>
                      {onCreateTask && (
                        <Tooltip title="Create Task">
                          <IconButton
                            size="small"
                            onClick={() => onCreateTask(p)}
                            sx={{ p: 0.25, color: "#2563EB" }}
                          >
                            <PlaylistAddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sorted.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          sx={{
            "& .MuiTablePagination-displayedRows": { fontSize: "0.75rem" },
          }}
        />
      )}
    </>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function StatusChip({ status }) {
  const colors = HEALTH_STATUS_COLORS[status] || HEALTH_STATUS_COLORS["No Status"];
  return (
    <Chip
      label={status || "—"}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.66rem",
        fontWeight: 600,
        bgcolor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
      }}
    />
  );
}

function VisitChip({ visit }) {
  const colors = VISIT_COLORS[visit];
  if (!colors) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
        {visit || "—"}
      </Typography>
    );
  }
  return (
    <Chip
      label={visit}
      size="small"
      sx={{
        height: 22,
        fontSize: "0.66rem",
        fontWeight: 600,
        bgcolor: colors.bg,
        color: colors.text,
      }}
    />
  );
}

function GapBadge({ count, isER }) {
  const val = count || 0;
  if (val === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
        0
      </Typography>
    );
  }
  const isHigh = isER ? val > 0 : val >= 3;
  const isMed = !isHigh && val >= 1;
  return (
    <Chip
      label={val}
      size="small"
      sx={{
        height: 20,
        minWidth: 28,
        fontSize: "0.68rem",
        fontWeight: 700,
        bgcolor: isHigh ? "#FEE2E2" : isMed ? "#FEF3C7" : "#F1F5F9",
        color: isHigh ? "#991B1B" : isMed ? "#92400E" : "#475569",
      }}
    />
  );
}

/** Chip con fecha de última visita SantePlus, color-coded por antigüedad */
function SpVisitChip({ date }) {
  const age = getSpVisitAge(date);
  const colors = SP_VISIT_COLORS[age];

  if (!date) {
    return (
      <Chip
        label="—"
        size="small"
        sx={{
          height: 20,
          fontSize: "0.64rem",
          fontWeight: 600,
          bgcolor: colors.bg,
          color: colors.text,
        }}
      />
    );
  }

  const d = new Date(date);
  const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.64rem",
        fontWeight: 600,
        bgcolor: colors.bg,
        color: colors.text,
      }}
    />
  );
}

/** Badge numérico para observaciones y tareas */
function CountBadge({ count, color, bgColor }) {
  const val = count || 0;
  if (val === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
        0
      </Typography>
    );
  }
  return (
    <Chip
      label={val}
      size="small"
      sx={{
        height: 20,
        minWidth: 24,
        fontSize: "0.68rem",
        fontWeight: 700,
        bgcolor: bgColor,
        color,
      }}
    />
  );
}

/** Badge "X/Y" para gaps trabajados vs open gaps, color según ratio */
function GapsWorkedBadge({ worked, total }) {
  const w = worked || 0;
  const t = total || 0;

  if (t === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
        —
      </Typography>
    );
  }

  const ratio = t > 0 ? w / t : 0;
  let bg, color;
  if (ratio >= 0.75) {
    bg = "#DCFCE7"; color = "#166534";
  } else if (ratio >= 0.25) {
    bg = "#FEF3C7"; color = "#92400E";
  } else {
    bg = "#FEE2E2"; color = "#991B1B";
  }

  return (
    <Chip
      label={`${w}/${t}`}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.64rem",
        fontWeight: 700,
        bgcolor: bg,
        color,
      }}
    />
  );
}
